import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { useCartStore, useWishlistStore } from '../store';
import { toast } from 'react-toastify';
import './ProductDetail.css';

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct]   = useState(null);
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [qty, setQty]           = useState(1);
  const [tab, setTab]           = useState('description');
  const { addItem }             = useCartStore();
  const { toggle, isInWishlist }= useWishlistStore();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/slug/${slug}`)
      .then(r => {
        setProduct(r.data.product);
        return api.get(`/products/${r.data.product.id}/reviews`);
      })
      .then(r => setReviews(r.data.reviews))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!product) return <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}><h2>Product not found</h2><Link to="/shop" className="btn btn-primary" style={{ marginTop: '1rem' }}>Back to Shop</Link></div>;

  const price = product.salePrice || product.price;
  const discount = product.salePrice ? Math.round((1 - product.salePrice / product.price) * 100) : 0;
  const isWished = isInWishlist(product.id);

  return (
    <div className="container pd-page">
      {/* Breadcrumb */}
      <div className="pd-breadcrumb">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <span>{product.name}</span>
      </div>

      {/* Main */}
      <div className="pd-main">
        {/* Image */}
        <div className="pd-gallery">
          <img src={product.images?.[0] || 'https://placehold.co/500x400?text=Peptide'} alt={product.name} />
          {discount > 0 && <span className="pd-discount">-{discount}% OFF</span>}
        </div>

        {/* Info */}
        <div className="pd-info">
          <p className="pd-category">{product.category}</p>
          <h1 className="pd-name">{product.name}</h1>
          <div className="pd-rating">
            <span className="stars">{'★'.repeat(Math.round(product.rating || 0))}{'☆'.repeat(5 - Math.round(product.rating || 0))}</span>
            <span className="text-gray text-sm">({product.reviewCount || 0} reviews)</span>
          </div>
          <div className="pd-price">
            <span className="pd-price__main">${price.toFixed(2)}</span>
            {product.salePrice && <span className="pd-price__orig line-through text-gray">${product.price.toFixed(2)}</span>}
          </div>

          <div className="pd-meta">
            <div><strong>Purity:</strong> {product.purity || '≥99%'}</div>
            <div><strong>Molecular Weight:</strong> {product.molecularWeight || 'N/A'} g/mol</div>
            <div><strong>In Stock:</strong> <span className="text-green">{product.stock > 0 ? `${product.stock} units` : 'Out of stock'}</span></div>
          </div>

          <div className="pd-sequence">
            <strong>Sequence:</strong>
            <code>{product.sequence || 'Available upon request'}</code>
          </div>

          <div className="pd-actions">
            <div className="pd-qty">
              <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
              <span>{qty}</span>
              <button onClick={() => setQty(q => q + 1)}>+</button>
            </div>
            <button className="btn btn-primary btn-lg" style={{ flex: 1 }} onClick={async () => { await addItem(product, qty); toast.success('Added to cart!'); }}>
              🛒 Add to Cart
            </button>
            <button className="btn btn-secondary" onClick={async () => { const a = await toggle(product); toast(a ? '❤️ Wishlisted' : 'Removed'); }} title="Wishlist">
              {isWished ? '❤️' : '🤍'}
            </button>
          </div>

          <div className="pd-trust">
            <span>🔬 3rd Party Tested</span>
            <span>🚚 Fast Shipping</span>
            <span>🔒 Secure Payment</span>
            <Link to="/certificates">View COA →</Link>
          </div>

          <div className="pd-disclaimer">
            ⚠️ For research use only. Not for human consumption.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="pd-tabs">
        <div className="pd-tab-nav">
          {['description', 'specifications', 'reviews'].map(t => (
            <button key={t} className={`pd-tab-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="pd-tab-content card">
          {tab === 'description' && <div><p>{product.description}</p></div>}
          {tab === 'specifications' && (
            <table className="pd-spec-table">
              <tbody>
                {[['Name', product.name], ['Purity', product.purity], ['Molecular Weight', `${product.molecularWeight} g/mol`], ['Category', product.category], ['Storage', '-20°C, protect from light'], ['Form', 'Lyophilized powder'], ['CAS Number', 'Available upon request']].map(([k, v]) => (
                  <tr key={k}><td><strong>{k}</strong></td><td>{v}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === 'reviews' && (
            <div className="pd-reviews">
              {reviews.length === 0 ? <p className="text-gray">No reviews yet. Be the first researcher to review this compound.</p> : reviews.map(r => (
                <div key={r.id} className="pd-review">
                  <div className="pd-review__header">
                    <strong>{r.userName}</strong>
                    <span className="stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                    <span className="text-gray text-sm">{new Date(r.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p>{r.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
